import { css } from '@emotion/react'
import { Button, Flow, HFlow, Icon, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { useAccordionControl } from 'components/accordion/useAccordionControl'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { useErrorHandler } from 'components/error'
import { SEM_EQUIPE_ITEM } from 'components/form'
import { PageContent } from 'components/layout/PageContent'
import { ACOMPANHAMENTO_TERRITORIO_PATH } from 'components/layout/SideMenu/SideMenuItems'
import { LoadingIndicator } from 'components/loading'
import { PageHeaderSection } from 'components/PageHeaderSection'
import { useFlags } from 'config/useFlagsContext'
import { useTerritorioMicroareaQuery, useVisualizacaoTerritorialQuery } from 'graphql/hooks.generated'
import { LembreteProfissionalIdentificadorEnum, PageParams, TipoEnderecoEnum } from 'graphql/types.generated'
import {
  LembreteProfissionalSearchBy,
  useLembreteProfissional,
} from 'hooks/lembrete-profissional/useLembreteProfissional'
import { Fragment, useRef, useState } from 'react'
import { useHistory } from 'react-router'
import { v4 } from 'uuid'
import { useCadastroImovelCache } from 'view/cadastro-imovel/hooks/useCadastroImovelCache'
import { AnimaisImovelFormModel, tipoEnderecoIndigena } from 'view/cadastro-imovel/model-cadastroImovel'
import { useSaudeIndigenaAuthorization } from 'view/cidadao/authorization/useSaudeIndigenaAuthorization'

import { MicroareaLogradouroPagedList } from './components/list/MicroareaLogradouroPagedList'
import { TerritorioContentSwitch, TerritorioViewTabEnum } from './components/list/TerritorioContentSwitch'
import { ModalNovidadeInativacaoImoveis } from './components/modal/ModalNovidadeInativacaoImoveis'
import { RelatorioAcompanhamentoTerritorio } from './components/RelatorioAcompanhamentoTerritorio'
import VisualizacaoTerritorialMicroareaTabs from './components/VisualizacaoTerritorialMicroareaTabs'
import {
  convertAnimaisImovelFilterToFormModel,
  convertVisualizacaoFormModelToInput,
  convertVisualizacaoMicroareaFilterToInput,
} from './converter'
import { useMicroareasVisualizacao } from './hooks/useMicroareasVisualizacao'
import { useVisualizacaoCache } from './hooks/useVisualizacaoCache'
import {
  TerritorioModel,
  VisualizacaoFilterModel,
  VisualizacaoMicroareasFilterFormModel,
  VisualizacaoTerritorialBuscaFormModel,
} from './model'
import { TerritorioMapasView } from './territorio-mapas/TerritorioMapasView'
import { VisualizacaoTerritorialBuscaForm } from './VisualizacaoTerritorialBuscaForm'

const INATIVOS_TAB_ID = '__INATIVOS__'

interface VisualizacaoTerritorialViewProps {
  activeTab: TerritorioViewTabEnum
}

export function VisualizacaoTerritorialView(props: Readonly<VisualizacaoTerritorialViewProps>) {
  const { activeTab } = props

  const handleRejection = useErrorHandler()
  const { acesso, profissional } = useAcessoLotacaoOrEstagio()
  const history = useHistory()
  const theme = useTheme()
  const styles = createStyles(theme)

  const [isModalDismissed, setIsModalDismissed] = useState(false)

  const {
    isAtivo: isModalNovidadeInativacaoImoveisAtivo,
    marcarVisualizado: marcarNovidadeInativacaoImoveisVisualizada,
  } = useLembreteProfissional({
    identificador: LembreteProfissionalIdentificadorEnum.MODAL_NOVIDADE_INATIVACAO_IMOVEIS,
    searchBy: LembreteProfissionalSearchBy.USUARIO,
  })

  const equipeIne = acesso?.equipe?.ine ?? '-'
  const unidadeSaude = acesso?.unidadeSaude
  const equipe = acesso?.equipe

  const { PILOTO_INATIVACAO_IMOVEIS_ENABLED, MAPA_TERRITORIO_ENABLED } = useFlags()
  const { hasTerritorioIndigenaPermission } = useSaudeIndigenaAuthorization()

  const shouldOpenNovidadeInativacaoModal =
    PILOTO_INATIVACAO_IMOVEIS_ENABLED && isModalNovidadeInativacaoImoveisAtivo && !isModalDismissed

  const isCoordenadorUbs = acesso.perfis?.some((perfil) => perfil?.nome === 'COORDENADOR DA UBS')
  const defaultTerritorioEquipeFilter: VisualizacaoTerritorialBuscaFormModel = {
    unidadeSaude: unidadeSaude,
    equipe: isCoordenadorUbs ? null : equipe ?? SEM_EQUIPE_ITEM,
    tipoEndereco: hasTerritorioIndigenaPermission ? TipoEnderecoEnum.ALDEIA : TipoEnderecoEnum.LOGRADOURO,
  }

  const progressToken = useRef(v4())
  const { deleteCacheState } = useCadastroImovelCache(acesso.id)

  const {
    visualizacaoCacheState,
    setVisualizacaoCacheState,
    deleteVisualizacaoCacheState,
    shouldUseCache,
  } = useVisualizacaoCache(acesso?.id)

  const [visualizacaoFilter, setVisualizacaoFilter] = useState<VisualizacaoFilterModel>(
    shouldUseCache
      ? visualizacaoCacheState.visualizacaoFilter
      : { territorioEquipeFilter: defaultTerritorioEquipeFilter }
  )

  const isTabListaDeImoveis = activeTab === TerritorioViewTabEnum.LISTA_DE_IMOVEIS

  const territorioEquipeFilter = visualizacaoFilter?.territorioEquipeFilter || defaultTerritorioEquipeFilter
  const skipQueryFirstRender =
    !isTabListaDeImoveis ||
    (tipoEnderecoIndigena.includes(territorioEquipeFilter.tipoEndereco as TipoEnderecoEnum) &&
      !territorioEquipeFilter.dsei &&
      !territorioEquipeFilter.poloBase) ||
    territorioEquipeFilter.equipe === null

  const { data, loading: loadingTerritorioEquipe } = useVisualizacaoTerritorialQuery({
    variables: {
      input: {
        ...convertVisualizacaoFormModelToInput(territorioEquipeFilter),
        stAtivo: true,
      },
    },
    skip: skipQueryFirstRender,
  })

  const microareas =
    data?.territorioEquipe?.microareas?.filter((microarea): microarea is string => microarea !== null) ?? []
  const poloBase = data?.territorioEquipe?.poloBase
  const hasInativos = !!data?.territorioEquipe?.hasInativos
  const hasLogradouros = microareas.length > 0 || !!poloBase || hasInativos

  const { handleAccordionItemClick, isExpanded, resetExpandedItems, expandedItems } = useAccordionControl({
    allowMultipleExpanded: true,
    initialExpandedItems: shouldUseCache ? visualizacaoCacheState?.expandedItems : [],
  })

  const {
    currentMicroareaTab,
    handleMicroareaTabClick,
    setRenderMicroareaControl,
    setCurrentMicroareaTab,
  } = useMicroareasVisualizacao(visualizacaoCacheState?.currentMicroareaTab, shouldUseCache, resetExpandedItems)
  const [pageParams, setPageParams] = useState<{ pageParams: PageParams }>(
    shouldUseCache ? { pageParams: visualizacaoCacheState?.pageParams } : initialPageParams
  )

  const handleStopRenderMicroareaCache = () => {
    resetExpandedItems()
    setRenderMicroareaControl((state) => ({ ...state, renderMicroareaCache: false }))
  }

  const onFiltrarMicroareas = (formValues: VisualizacaoMicroareasFilterFormModel) => {
    handleStopRenderMicroareaCache()
    setVisualizacaoFilter((state) => ({
      ...state,
      territorioMicroareasFilter: {
        ...formValues,
        isCadastroAtualizadoMenosDeUmAno: formValues?.isCadastroAtualizadoMenosDeUmAno?.atualizadoHaMenosDeUmAno,
        statusCadastroImovel: formValues?.statusCadastroImovel,
        isImovelVisitadoMenosDeUmMes: formValues?.isImovelVisitadoMenosDeUmMes?.ultimaVisitaHaMenosDeUmMes,
        animaisImovel: convertAnimaisImovelFilterToFormModel(formValues?.animaisImovelFilter as AnimaisImovelFormModel),
        condicoesMoradia: formValues?.condicoesMoradia,
        tipoImovel: formValues?.tipoImovel,
        possuiInconsistencia: formValues?.possuiInconsistencia,
      },
    }))
  }

  const onBuscarTerritorioEquipe = (formValues: VisualizacaoTerritorialBuscaFormModel) => {
    handleStopRenderMicroareaCache()
    setCurrentMicroareaTab(undefined)
    setVisualizacaoFilter((state) => ({ ...state, territorioEquipeFilter: { ...formValues } }))
  }

  const saveVisualizacaoCacheState = () =>
    setVisualizacaoCacheState({
      visualizacaoFilter,
      currentMicroareaTab,
      pageParams: pageParams.pageParams,
      expandedItems,
      isRedirect: true,
    })

  const resolveCurrentTabSelection = (
    currentMicroareaTab: string | undefined,
    hasInativos: boolean,
    microareas: readonly string[],
    poloBaseId: string | undefined
  ) => {
    if (hasInativos && currentMicroareaTab === INATIVOS_TAB_ID) {
      return INATIVOS_TAB_ID
    }

    const isPoloBaseTab = currentMicroareaTab && poloBaseId === currentMicroareaTab

    const defaultTab = hasInativos ? INATIVOS_TAB_ID : undefined

    const isValidMicroareaTab = (currentMicroareaTab && microareas?.includes(currentMicroareaTab)) || isPoloBaseTab

    const nextTab = poloBaseId ?? microareas?.[0] ?? defaultTab

    return isValidMicroareaTab ? currentMicroareaTab : nextTab
  }

  const currentTab = resolveCurrentTabSelection(currentMicroareaTab, hasInativos, microareas, poloBase?.id)

  const isInativosCurrentTab = currentTab === INATIVOS_TAB_ID

  const isPoloBaseCurrentTab = poloBase && currentTab === poloBase?.id

  const microareaInput = {
    cnes: unidadeSaude.cnes,
    ine: equipeIne,
    tipoEndereco: territorioEquipeFilter.tipoEndereco,
    ...convertVisualizacaoFormModelToInput(visualizacaoFilter?.territorioEquipeFilter),
    ...convertVisualizacaoMicroareaFilterToInput(visualizacaoFilter.territorioMicroareasFilter),
    microarea: isInativosCurrentTab || isPoloBaseCurrentTab ? undefined : currentTab,
    pageParams: pageParams.pageParams,
    stAtivo: !isInativosCurrentTab,
  }

  const { data: territorioMicroareaData, loading: loadingTerritorioMicroarea, refetch } = useTerritorioMicroareaQuery({
    variables: {
      input: microareaInput,
    },
    skip:
      (territorioEquipeFilter.tipoEndereco === TipoEnderecoEnum.LOGRADOURO &&
        microareaInput.microarea === undefined &&
        !isInativosCurrentTab) ||
      currentTab === undefined,
    fetchPolicy: 'network-only',
  })

  const handleRefreshMicroarea = () => {
    refetch({ input: microareaInput })
  }

  const handleClickAdicionarImovel = () => {
    deleteCacheState(false)
    saveVisualizacaoCacheState()
    setTimeout(() => history.push(`${ACOMPANHAMENTO_TERRITORIO_PATH}/cadastrarImovel?redirect=true`))
  }

  const handleCloseModalNovidadeInativacaoImoveis = () => {
    if (isModalDismissed) {
      return
    }

    setIsModalDismissed(true)
    marcarNovidadeInativacaoImoveisVisualizada().catch(handleRejection)
  }

  const territorioModel: TerritorioModel = {
    cnes: unidadeSaude.cnes,
    ine: equipeIne,
    microarea: isInativosCurrentTab || isPoloBaseCurrentTab ? undefined : currentTab,
    stAtivo: !isInativosCurrentTab,
    ...convertVisualizacaoMicroareaFilterToInput(visualizacaoFilter?.territorioMicroareasFilter),
    ...convertVisualizacaoFormModelToInput(visualizacaoFilter.territorioEquipeFilter),
  }

  const hasImoveisLogradouro = territorioMicroareaData?.territorioMicroarea?.logradouros?.content.isNotEmpty()

  const isTerritorioFiltroDiferenteUsuarioLogado =
    unidadeSaude.cnes !== territorioEquipeFilter?.unidadeSaude?.cnes ||
    equipeIne !== territorioEquipeFilter?.equipe?.ine

  const isTerritorioFiltradoAtivo =
    territorioEquipeFilter.equipe?.ativo !== false && territorioEquipeFilter?.unidadeSaude?.ativo !== false

  const renderMicroareaTabs = () => {
    if (hasLogradouros) {
      return (
        <VisualizacaoTerritorialMicroareaTabs
          microareas={microareas}
          poloBase={poloBase}
          hasInativos={hasInativos}
          inativosTabId={INATIVOS_TAB_ID}
          currentMicroareaTab={currentTab}
          onTabClick={handleMicroareaTabClick}
        />
      )
    }

    if (loadingTerritorioEquipe) {
      return <LoadingIndicator />
    }

    return (
      <Text fontWeight='normal' variant='h4' fontStyle='italic'>
        Ainda não existem imóveis cadastrados sob essa responsabilidade. Clique no botão acima para iniciar um novo
        registro.
      </Text>
    )
  }

  const renderMicroareaContent = () => {
    if (loadingTerritorioMicroarea && !loadingTerritorioEquipe) {
      return <LoadingIndicator />
    }

    if (!currentTab || !hasLogradouros) {
      return null
    }

    return (
      <Fragment>
        <MicroareaLogradouroPagedList
          progressToken={progressToken.current}
          logradouros={territorioMicroareaData?.territorioMicroarea}
          territorio={territorioModel}
          stAtivo={!isInativosCurrentTab}
          showConsolidatedIndices={!isInativosCurrentTab}
          onAccordionItemClick={handleAccordionItemClick}
          isExpanded={isExpanded}
          resetExpandedItems={resetExpandedItems}
          onFiltrarMicroareas={onFiltrarMicroareas}
          microareasFilter={visualizacaoFilter?.territorioMicroareasFilter}
          lotacaoId={acesso?.id}
          municipioId={acesso?.municipio.id}
          profissionalId={profissional?.id}
          saveVisualizacaoCacheState={saveVisualizacaoCacheState}
          onChangePageParams={setPageParams}
          deleteVisualizacaoCacheState={deleteVisualizacaoCacheState}
          onRefreshMicroarea={handleRefreshMicroarea}
          tipoEndereco={territorioEquipeFilter.tipoEndereco}
          isTerritorioFiltroDiferenteUsuarioLogado={isTerritorioFiltroDiferenteUsuarioLogado}
          canCboWriteFicha={acesso.cbo.actions.cadastroDomiciliar.enabled}
          isTerritorioFiltradoAtivo={isTerritorioFiltradoAtivo}
        />
        <RelatorioAcompanhamentoTerritorio
          usuarioId={profissional.usuario.id}
          microareaInput={microareaInput}
          hasImoveisLogradouros={hasImoveisLogradouro}
          isInativosCurrentTab={isInativosCurrentTab}
        />
      </Fragment>
    )
  }

  return (
    <Fragment>
      <ModalNovidadeInativacaoImoveis
        open={shouldOpenNovidadeInativacaoModal}
        onClose={handleCloseModalNovidadeInativacaoImoveis}
      />

      <Flow direction='vertical' gap={0} style={styles.pageFlow}>
        <div
          css={[styles.pageHeader, activeTab === TerritorioViewTabEnum.MAPA_DO_TERRITORIO && styles.pageHeaderBorder]}
        >
          <PageHeaderSection title='Acompanhamento do território' style={styles.header}>
            {MAPA_TERRITORIO_ENABLED && <TerritorioContentSwitch baseUrl={ACOMPANHAMENTO_TERRITORIO_PATH} />}
          </PageHeaderSection>
        </div>

        {activeTab === TerritorioViewTabEnum.LISTA_DE_IMOVEIS && (
          <PageContent type='filled'>
            <VFlow vSpacing={3}>
              <VisualizacaoTerritorialBuscaForm
                onSubmit={onBuscarTerritorioEquipe}
                appliedFilter={territorioEquipeFilter}
                defaultFilter={defaultTerritorioEquipeFilter}
                tabContext={TerritorioViewTabEnum.LISTA_DE_IMOVEIS}
              />

              <Button
                kind='primary'
                skin='ghost'
                onClick={handleClickAdicionarImovel}
                style={css`
                  width: 100%;
                  border: 1px solid ${theme.pallete.primary.c40};
                  margin-top: 1.5rem;
                `}
              >
                <HFlow>
                  <Icon icon='plus' />
                  Adicionar imóvel
                </HFlow>
              </Button>

              {renderMicroareaTabs()}
            </VFlow>
            {renderMicroareaContent()}
          </PageContent>
        )}

        {MAPA_TERRITORIO_ENABLED && activeTab === TerritorioViewTabEnum.MAPA_DO_TERRITORIO && <TerritorioMapasView />}
      </Flow>
    </Fragment>
  )
}

const createStyles = (theme: Theme) => ({
  pageFlow: css`
    grid-auto-rows: auto 1fr;
    flex: 1;
    min-height: 0;
  `,
  pageHeader: css`
    padding-top: 2rem;
    padding-bottom: 1rem;
  `,
  pageHeaderBorder: css`
    border-bottom: 1px solid ${theme.pallete.divider};
  `,
  header: css`
    padding-bottom: 1rem;
  `,
})

const initialPageParams = { pageParams: { page: 0, size: 10 } }
